
import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ProductVariant } from '@/types/product';

export interface CartItem {
  id: string;
  name: string;
  variant: ProductVariant;
  quantity: number;
  price: number;
  image: string;
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, change: number) => void;
  clearCart: () => void;
}

const CART_STORAGE_KEY = 'agroref_cart';

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>(() => {
    // Charger le panier depuis le localStorage au démarrage
    const savedCart = localStorage.getItem(CART_STORAGE_KEY);
    console.log('Cart: Loading from localStorage', { savedCart });
    if (savedCart) {
      try {
        return JSON.parse(savedCart);
      } catch (error) {
        console.error('Cart: Error parsing saved cart', error);
        return [];
      }
    }
    return [];
  });

  // Sauvegarder le panier dans le localStorage à chaque modification
  useEffect(() => {
    console.log('Cart: Saving to localStorage', { 
      itemCount: items.length,
      totalValue: items.reduce((sum, item) => sum + item.price * item.quantity, 0)
    });
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (newItem: CartItem) => {
    if (!newItem.id || !newItem.name || !newItem.variant || !newItem.price) {
      console.error('Cart: Invalid item data', newItem);
      return;
    }

    console.log('Cart: Adding item', {
      itemId: newItem.id,
      variant: newItem.variant,
      quantity: newItem.quantity
    });

    setItems(currentItems => {
      const existingItemIndex = currentItems.findIndex(
        item => item.id === newItem.id && 
               item.variant.size === newItem.variant.size && 
               item.variant.colorName === newItem.variant.colorName
      );

      if (existingItemIndex > -1) {
        console.log('Cart: Updating existing item quantity', {
          itemId: newItem.id,
          oldQuantity: currentItems[existingItemIndex].quantity,
          addedQuantity: newItem.quantity
        });

        const updatedItems = [...currentItems];
        updatedItems[existingItemIndex].quantity += newItem.quantity;
        return updatedItems;
      }

      console.log('Cart: Adding new item to cart');
      return [...currentItems, newItem];
    });
  };

  const removeItem = (itemId: string) => {
    if (!itemId) {
      console.error('Cart: Invalid item ID for removal');
      return;
    }

    console.log('Cart: Removing item', { itemId });
    setItems(currentItems => currentItems.filter(item => item.id !== itemId));
  };

  const updateQuantity = (itemId: string, change: number) => {
    if (!itemId) {
      console.error('Cart: Invalid item ID for quantity update');
      return;
    }

    console.log('Cart: Updating quantity', { 
      itemId, 
      change,
    });

    setItems(currentItems =>
      currentItems.map(item => {
        if (item.id === itemId) {
          const newQuantity = Math.max(1, item.quantity + change);
          console.log('Cart: New quantity calculated', {
            itemId,
            oldQuantity: item.quantity,
            newQuantity
          });
          return { ...item, quantity: newQuantity };
        }
        return item;
      })
    );
  };

  const clearCart = () => {
    console.log('Cart: Clearing all items');
    setItems([]);
  };

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQuantity, clearCart }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
